import { useEffect, useState, useRef } from "react";
import api, { apiErrorMsg, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, Upload, Users, AlertTriangle, CheckCircle2, FileSpreadsheet, Info } from "lucide-react";

export default function CelLeadsAcao() {
  const fileRef = useRef(null);
  const [vendedoras, setVendedoras] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ nome: "", data: new Date().toISOString().slice(0, 10), descricao: "", vendedora_id: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/cel/vendedoras"),
      api.get("/cel/acoes"),
    ])
      .then(([r1, r2]) => { setVendedoras(r1.data || []); setAcoes(r2.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const enviar = async () => {
    if (!form.nome.trim() || !form.data || !form.vendedora_id || !file) {
      toast.error("Preencha nome da ação, data, vendedora e anexe a planilha.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("nome", form.nome);
      fd.append("data", form.data);
      fd.append("descricao", form.descricao || "");
      fd.append("vendedora_id", form.vendedora_id);
      fd.append("file", file);
      const r = await api.post("/cel/acoes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUltimoResultado(r.data);
      toast.success(`${r.data.acao.qtd_gerados} lead(s) importado(s) · ${r.data.acao.qtd_duplicados} duplicado(s) · ${r.data.acao.qtd_erros} erro(s)`);
      setForm({ nome: "", data: new Date().toISOString().slice(0, 10), descricao: "", vendedora_id: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Falha ao importar leads."));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="cel-leads-acao-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Megaphone className="w-7 h-7 text-sky-600" /> Leads de Ação · CeL
        </h1>
        <p className="text-sm text-slate-500 mt-1">Suba planilhas de leads de eventos/campanhas — cada linha vira uma indicação já atribuída à vendedora escolhida.</p>
      </div>

      {/* Instruções da planilha */}
      <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <div className="font-bold text-sky-900 mb-1">Formato da planilha (.xlsx)</div>
            <div>Cabeçalhos obrigatórios (linha 1): <b>municipio</b> · <b>uf</b> · <b>contato_nome</b> · <b>contato_whatsapp</b></div>
            <div>Opcionais: <span className="font-mono text-xs">cargo</span> (ou <span className="font-mono text-xs">funcao</span>), <span className="font-mono text-xs">valor_estimado</span>, <span className="font-mono text-xs">email</span>, <span className="font-mono text-xs">observacoes</span></div>
            <div className="mt-1 text-xs text-slate-600">Duplicatas (mesmo município/UF já ativo no funil) são ignoradas automaticamente.</div>
          </div>
        </div>
      </div>

      {/* Form Upload */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Nome da ação *</Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: Feira SC 2026" data-testid="acao-nome" />
          </div>
          <div>
            <Label>Data *</Label>
            <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="acao-data" />
          </div>
          <div className="md:col-span-2">
            <Label>Descrição / contexto</Label>
            <Textarea rows={2} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="O que foi essa ação, público, objetivo..." />
          </div>
          <div>
            <Label>Vendedora destino *</Label>
            <Select value={form.vendedora_id} onValueChange={(v) => setForm({ ...form, vendedora_id: v })}>
              <SelectTrigger data-testid="acao-vendedora"><SelectValue placeholder="Escolha..." /></SelectTrigger>
              <SelectContent>
                {vendedoras.map((v) => <SelectItem key={v.id} value={v.id}>{v.nome} · {(v.ufs_atuacao || []).join("/") || "s/UFs"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label>Planilha Excel *</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-700 border border-slate-300 rounded-md file:mr-3 file:rounded-l-md file:border-0 file:bg-sky-600 file:text-white file:px-3 file:py-2 file:font-semibold hover:file:bg-sky-700"
              data-testid="acao-file"
            />
            {file && <div className="text-xs text-slate-600 mt-1"><FileSpreadsheet className="inline w-3 h-3 mr-1" />{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>
        </div>
        <Button onClick={enviar} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="acao-submit">
          <Upload className="w-4 h-4 mr-1" /> {busy ? "Importando..." : "Importar leads"}
        </Button>
      </div>

      {/* Resultado do último upload */}
      {ultimoResultado && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <h2 className="font-bold text-lg">Resultado da importação · {ultimoResultado.acao.nome}</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Criados</div>
              <div className="text-2xl font-bold text-emerald-800">{ultimoResultado.acao.qtd_gerados}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Duplicados</div>
              <div className="text-2xl font-bold text-amber-800">{ultimoResultado.acao.qtd_duplicados}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-bold text-red-700 tracking-wider">Erros</div>
              <div className="text-2xl font-bold text-red-800">{ultimoResultado.acao.qtd_erros}</div>
            </div>
          </div>
          {ultimoResultado.duplicados.length > 0 && (
            <div className="text-xs text-slate-600">
              <b className="text-amber-700">Duplicados:</b>{" "}
              {ultimoResultado.duplicados.slice(0, 8).map((d) => `${d.municipio} (${d.no_funil})`).join(", ")}
              {ultimoResultado.duplicados.length > 8 && ` ...+${ultimoResultado.duplicados.length - 8}`}
            </div>
          )}
          {ultimoResultado.erros.length > 0 && (
            <div className="text-xs text-slate-600">
              <b className="text-red-700">Erros:</b>{" "}
              {ultimoResultado.erros.slice(0, 5).map((e) => `linha ${e.linha}: ${e.motivo}`).join(" · ")}
            </div>
          )}
        </div>
      )}

      {/* Histórico de ações */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="w-5 h-5 text-slate-600" />
          <h2 className="text-lg font-bold text-slate-900">Ações anteriores</h2>
        </div>
        {loading ? (
          <div className="text-center py-6 text-slate-400 text-sm">Carregando...</div>
        ) : acoes.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm">Nenhuma ação registrada.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                <th className="py-2 pr-2">Data</th>
                <th className="py-2 pr-2">Nome</th>
                <th className="py-2 pr-2">Vendedora</th>
                <th className="py-2 pr-2 text-center">Leads</th>
                <th className="py-2 pr-2 text-center">Dupl.</th>
                <th className="py-2 pr-2 text-center">Erros</th>
                <th className="py-2 pr-2">Criado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {acoes.map((a) => (
                <tr key={a.id} data-testid={`acao-row-${a.id}`}>
                  <td className="py-2 pr-2 font-mono text-xs">{a.data}</td>
                  <td className="py-2 pr-2 font-semibold text-slate-800">{a.nome}</td>
                  <td className="py-2 pr-2 text-slate-600">{a.vendedora_nome}</td>
                  <td className="py-2 pr-2 text-center font-bold text-emerald-700">{a.qtd_gerados}</td>
                  <td className="py-2 pr-2 text-center text-amber-700">{a.qtd_duplicados}</td>
                  <td className="py-2 pr-2 text-center text-red-700">{a.qtd_erros}</td>
                  <td className="py-2 pr-2 text-xs text-slate-500">{formatDateTime(a.criado_em)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
